import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LogOut, Plus } from "lucide-react";
import { api, fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STATUS_COLORS } from "../lib/status";

const STEPS = ["New", "Quoted", "Paid", "In Production", "Completed"];
const LABELS = { New: "Received", Quoted: "Quote sent", Paid: "Paid", "In Production": "In production", Completed: "Completed", Cancelled: "Cancelled" };

const Progress = ({ status }) => {
  if (status === "Cancelled") return null;
  const idx = STEPS.indexOf(status);
  return (
    <div className="mt-5 flex gap-1.5" aria-hidden>
      {STEPS.map((s, i) => <div key={s} className={`h-1.5 flex-1 rounded-full ${i <= idx ? "bg-[#FF1F8E]" : "bg-[#FFF4F8]"}`} />)}
    </div>
  );
};

const OrderCard = ({ o }) => (
  <article className="rounded-3xl bg-white p-6 ring-1 ring-[#EBD08A]" data-testid={`my-order-${o.id}`}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h3 className="font-display text-xl font-bold">{o.product_type} · Qty {o.quantity}</h3>
        <p className="text-sm text-black/50">Sent {new Date(o.created_at).toLocaleDateString()}{o.needed_by && ` · Needed by ${o.needed_by}`}</p>
      </div>
      <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_COLORS[o.status]}`} data-testid={`my-order-status-${o.id}`}>{LABELS[o.status]}</span>
    </div>
    <Progress status={o.status} />
    <p className="mt-4 whitespace-pre-wrap text-sm text-black/70">{o.design_idea}</p>
    {o.sizes_colors && <p className="mt-2 text-sm text-black/55">Sizes & colors: {o.sizes_colors}</p>}
    {o.reference_images.length > 0 && (
      <div className="mt-4 flex gap-2">
        {o.reference_images.map((p) => <img key={p} src={fileUrl(p)} alt="Reference" className="h-16 w-16 rounded-lg object-cover ring-1 ring-[#EBD08A]" />)}
      </div>
    )}
  </article>
);

export default function MyOrders() {
  const { user, logout } = useAuth();
  const [orders, setOrders] = useState(null);
  useEffect(() => { api.get("/my/orders").then((r) => setOrders(r.data)).catch(() => setOrders([])); }, []);

  return (
    <section className="min-h-[70vh] bg-[#FFF4F8]">
      <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="font-script text-4xl text-[#FF1F8E]">Hi {user.name.split(" ")[0]},</p>
            <h1 className="font-display text-3xl font-bold sm:text-4xl" data-testid="my-orders-title">My orders</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/order" className="btn-pink !py-2.5" data-testid="my-orders-new-btn"><Plus className="h-4 w-4" />New request</Link>
            <button onClick={logout} className="btn-outline-gold !py-2.5" data-testid="customer-logout-btn"><LogOut className="h-4 w-4" />Sign out</button>
          </div>
        </div>
        <div className="stitch-line my-8" />
        {orders === null && <p className="text-sm text-black/50">Loading…</p>}
        {orders?.length === 0 && (
          <div className="rounded-3xl bg-white p-10 text-center" data-testid="my-orders-empty">
            <p className="font-display text-xl font-bold">No requests yet</p>
            <p className="mt-2 text-sm text-black/60">Requests you send while signed in will show up here with their status.</p>
            <Link to="/order" className="btn-pink mt-6">Start a Custom Order</Link>
          </div>
        )}
        <div className="space-y-4" data-testid="my-orders-list">{orders?.map((o) => <OrderCard key={o.id} o={o} />)}</div>
      </div>
    </section>
  );
}
